/**
 * Monster model registry: module creature ids / display names -> Meshy clip
 * registry + scale. The production source the renderer resolves monster
 * visuals from (fixtures stay for harness presets). B2's coverage check
 * walks module rosters against this registry and fails the rollout gate on
 * any hole, so gaps are DATA, not silent fallbacks.
 *
 * Clip files: /dev/api_<family>_<clip>.glb (one GLB per clip, shared rig).
 */

import type { ClipRegistry } from './clips';

function family(prefix: string, clips: string[]): ClipRegistry {
  return Object.fromEntries(clips.map((c) => [c, `/dev/api_${prefix}_${c}.glb`]));
}

/** Animation-only clip lane (Q20, extended to monsters 2026-07-22): the
 * mesh ships ONCE in the family base; clip GLBs carry only their track. */
const base = (prefix: string): string => `/dev/api_${prefix}_base.glb`;

/** Full Meshy clip families that exist on disk today. */
const GOBLIN = family('gob', [
  'alert',
  'idle',
  'walk',
  'run',
  'sword_slash',
  'slash_up',
  'combo',
  'block',
  'hit_react',
  'dead',
  'arise',
]);
const KOBOLD = family('kob', [
  'alert',
  'idle',
  'walk',
  'run',
  'sword_slash',
  'thrust_slash',
  'block',
  'hit_react',
  'dead',
]);
const SKELETON = family('skel', [
  'alert',
  'idle',
  'walk',
  'run',
  'sword_slash',
  'slash_up',
  'combo',
  'block',
  'hit_react',
  'dead',
  'arise',
]);
const SPECTER = family('spec', [
  'alert',
  'idle',
  'walk',
  'run',
  'attack',
  'hit_react',
  'dead',
  'arise',
]);
/** Partial families (registered so the coverage check reports the missing
 * clips instead of a missing monster). */
const DRAGON = family('drag', ['idle', 'bite']);
const WOLF = family('wolf', ['rest', 'walk']);

export interface MonsterModel {
  clips: ClipRegistry;
  /** Family base body GLB (mesh+skin; clips bind onto it by node name). */
  model: string;
  scale: number;
}

/**
 * Explicit creature entries (module `creature` ids and named bosses).
 * Scales are relative to the baked model size (true meters).
 */
const MONSTERS: Record<string, MonsterModel> = {
  goblin: { clips: GOBLIN, model: base('gob'), scale: 1.0 },
  kobold: { clips: KOBOLD, model: base('kob'), scale: 1.0 },
  skeleton: { clips: SKELETON, model: base('skel'), scale: 1.0 },
  specter: { clips: SPECTER, model: base('spec'), scale: 1.0 },
  frost_wolf: { clips: WOLF, model: base('wolf'), scale: 1.0 },
  // Goldvale named bosses: Frostfang (dire frost wolf), Vyrkora (adult
  // white dragon), plus the dragon age tiers.
  frostfang: { clips: WOLF, model: base('wolf'), scale: 1.3 },
  young_white_dragon: { clips: DRAGON, model: base('drag'), scale: 1.0 },
  wyrmling_white_dragon: { clips: DRAGON, model: base('drag'), scale: 0.6 },
  vyrkora: { clips: DRAGON, model: base('drag'), scale: 1.35 },
};

/** Keyword fallback for display names ("Skeleton 2", "Frost Wolf"). Order
 * matters: first match wins. */
const KEYWORDS: [string, string][] = [
  ['skeleton', 'skeleton'],
  ['specter', 'specter'],
  ['spectre', 'specter'],
  ['kobold', 'kobold'],
  ['goblin', 'goblin'],
  ['frostfang', 'frostfang'],
  ['wolf', 'frost_wolf'],
  ['vyrkora', 'vyrkora'],
  ['wyrmling', 'wyrmling_white_dragon'],
  ['dragon', 'young_white_dragon'],
];

const normalize = (s: string): string => s.toLowerCase().replace(/[\s-]+/g, '_');

/**
 * Resolve a creature id or display name to its registered model. Exact id
 * first, then keyword match. Null = unregistered (primitive proxy renders;
 * the B2 coverage check flags it).
 */
export function monsterModelFor(nameOrId: string): MonsterModel | null {
  const key = normalize(nameOrId);
  if (MONSTERS[key]) return MONSTERS[key];
  const spaced = nameOrId.toLowerCase();
  for (const [needle, id] of KEYWORDS) {
    if (spaced.includes(needle) || key.includes(needle)) return MONSTERS[id];
  }
  return null;
}

/** Clip tags a family is missing for full beat coverage (coverage check). */
export const REQUIRED_MONSTER_CLIP_ROLES = ['idle', 'walk', 'attack', 'hit', 'death'] as const;
